import Hr from '@creator/ui/components/Hr/Hr';
import MenuItem, { type MenuItemModel } from '@creator/ui/components/Menu/MenuItem';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import { getUrlPath } from '@creator/ui/utils/url-utils';
import A from '@src/basic-components/A/A';
import Icon from '@src/basic-components/Icon/Icon';
import { OffCanvasMenu } from '@src/components/OffCanvasMenu/OffCanvasMenu';
import { OffCanvasMenuClose } from '@src/components/OffCanvasMenu/OffCanvasMenuClose';
import { OffCanvasMenuContent } from '@src/components/OffCanvasMenu/OffCanvasMenuContent';
import { OffCanvasMenuTrigger } from '@src/components/OffCanvasMenu/OffCanvasMenuTrigger';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { ensureTokenUrl, getDomainConfigValue } from '@src/model/config/helpers';
import { useStoreActions, useStoreState } from '@src/model/hooks';
import { ensureUserUrl, getMergeableUsers } from '@src/model/user/helpers';
import { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import AppVersion from '../../AppVersion/AppVersion';
import ForgotPassword from '../../Auth/ForgotPassword/ForgotPassword';
import MergeAccountWrapper from '../../Auth/MergeAccountWrapper/MergeAccountWrapper';
import DarkModeToggle from '../../DarkModeToggle/DarkModeToggle';
import ProfileImage from '../../ProfileImage/ProfileImage';
import DomainMainMenu from './DomainMainMenu';

export interface MainMenuProps {
    className?: string;
    tokenName?: string;
}

const MainMenu: React.FC<MainMenuProps> = props => {
    const { tokenName = '' } = props;

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { handleModal } = useContext<UseModal>(ModalContext);

    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const myUser = useStoreState(state => state.user.myUser);
    const displayName = myUser?.displayName || '';
    const logout = useStoreActions(actions => actions.user.logout);
    const myFirebaseUser = useStoreState(state => state.user.myFirebaseUser);
    const myLoginUsers = useStoreState(state => state.user.myLoginUsers);

    function onLogoutClick() {
        logout();
        navigate(`/bbs/login?next=${getUrlPath()}`);
    }

    function onSearchClick() {
        if (tokenName) return navigate(`/bbs/${ensureTokenUrl(tokenName, false)}/search`);
        return navigate('/bbs/search');
    }

    function getMyProfileLink() {
        if (tokenName) return `/${ensureTokenUrl(tokenName)}/author/${ensureUserUrl(displayName)}`;

        const defaultTokenName = getDomainConfigValue('defaultTokenName') || '';
        return `/${ensureTokenUrl(defaultTokenName)}/author/${ensureUserUrl(displayName)}`;
    }

    function renderMergeAccount() {
        return <MergeAccountWrapper />;
    }

    function renderForgotPassword() {
        if (!myUser) return null;
        return <ForgotPassword onSuccess={() => handleModal(null)} userId={myUser.id} displayName={myUser.displayName} firebaseUser={myFirebaseUser} />;
    }

    function renderMenuItem(key: string, onClick?: () => void) {
        const item: MenuItemModel = {
            id: key,
            title: t(`mainMenu.${key}`),
        };
        return <MenuItem item={item} className="" onClick={onClick} />;
    }

    function renderTrigger() {
        return (
            <div className="flex items-center text-2xl cursor-pointer text-gray-50">
                <Icon name="menu" />
            </div>
        );
    }

    function renderProfileSection() {
        if (!myUser) return <div className="h-14" />;
        return (
            <div className="flex items-center justify-start px-5 py-4 space-x-4">
                <ProfileImage className="w-12 h-12" displayName={displayName} />
                <div className="flex flex-col overflow-hidden text-gray-900 dark:text-gray-50">
                    <UserDisplayName hideDomainSymbol={false} displayName={displayName} textProps={{ className: 'block truncate text-md font-bold' }} />
                    <div className="flex mt-1 text-xs">
                        <Text onClick={() => navigate(getMyProfileLink())} className="link">
                            {t('mainMenu.profileSection.viewProfileButton')}
                        </Text>
                        <Text className="mx-1 font-bold">·</Text>
                        <Text onClick={onLogoutClick} className="link">
                            {t('mainMenu.accountSection.logout')}
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    function renderForumPickerMenuItem() {
        if (!getDomainConfigValue('enableForumPicker')) return null;
        return renderMenuItem('forumPicker', () => navigate('/bbs/picker'));
    }

    function renderSearchMenuItem() {
        return renderMenuItem('search', onSearchClick);
    }

    function renderMyBoardsMenuItem() {
        if (!isLoggedIn) return null;
        return renderMenuItem('myBoards', () => navigate('/bbs/?tab=myBbs'));
    }

    function hasMergeableAccounts(): boolean {
        if (!myLoginUsers || !myUser) return false;

        const loginUsers = myLoginUsers.filter(user => !user.forwardTo || user.mainAccount);
        const mergeableUsers = getMergeableUsers(loginUsers);

        if (!mergeableUsers.length) return false;

        const mainAccount = loginUsers.find(user => user.mainAccount);
        if (mainAccount && !myUser.mainAccount) return false;

        const nonTokenOwnersBesidesCurrentUser = loginUsers.filter(user => (!user.tokenOwner || !user.tokenOwner.length) && user.id !== myUser.id);
        if (!nonTokenOwnersBesidesCurrentUser.length) return false;

        return true;
    }

    function renderAccountMenuItem() {
        if (!isLoggedIn) return null;

        const accountMenuItems: MenuItemModel[] = [
            {
                id: 'accountSection.editProfile',
                title: t('mainMenu.accountSection.editProfile'),
                url: '',
                onClick: () => navigate(`${getMyProfileLink()}?editProfile=true`),
            },
            {
                id: 'accountSection.changePassword',
                title: t('mainMenu.accountSection.changePassword'),
                url: '',
                onClick: () => handleModal(renderForgotPassword()),
            },
            {
                id: 'accountSection.accountSettings',
                title: t('mainMenu.accountSection.accountSettings'),
                url: '',
                onClick: () => navigate('/bbs/my-account/settings'),
            },
        ];

        // Only add merge accounts option if user has accounts to merge
        if (hasMergeableAccounts()) {
            accountMenuItems.push({
                id: 'accountSection.mergeAccounts',
                title: t('mainMenu.accountSection.mergeAccounts'),
                url: '',
                onClick: () => handleModal(renderMergeAccount()),
            });
        }

        // Define the parent account menu item.
        const accountParent: MenuItemModel = {
            id: 'accountSection',
            title: t('mainMenu.accountSection.title'),
            url: '',
            children: accountMenuItems,
        };

        return <MenuItem item={accountParent} className="" />;
    }

    function renderDarkModeToggle() {
        return (
            <div className="flex justify-start px-4 py-2">
                <DarkModeToggle className="" />
            </div>
        );
    }

    function renderPrivacyPolicyLink() {
        const url = getDomainConfigValue('privacyPolicyLink');
        if (!url) return null;
        return (
            <A className="" target="_blank" href={url}>
                {t('footerPrivacyPolicyLink')}
            </A>
        );
    }

    function renderTermsOfUseLink() {
        const url = getDomainConfigValue('termsOfUseLink');
        if (!url) return null;
        return (
            <A className="" target="_blank" href={url}>
                {t('termsOfUseLink')}
            </A>
        );
    }

    function renderFaqLink() {
        const url = getDomainConfigValue('faqLink');
        if (!url) return null;
        return (
            <A className="" target="_blank" href={url}>
                {t('footerFaqLink')}
            </A>
        );
    }

    function renderDomainMainMenu() {
        return <DomainMainMenu className="mt-2 text-sm" />;
    }

    function renderStakingButton() {
        if (!getDomainConfigValue('enableStakingButton')) return;
        return renderMenuItem('stakeRtb', () => window.open('https://staking.rtb.io', '_blank'));
    }

    function renderNetworkAdminDashboard() {
        if (!myUser) return null;
        const { networkAdmin } = myUser;
        if (!networkAdmin) return null;
        return renderMenuItem('networkAdminDashboard', () => navigate('/bbs/network-admin-dashboard'));
    }

    return (
        <OffCanvasMenu align="left">
            <OffCanvasMenuTrigger>{renderTrigger()}</OffCanvasMenuTrigger>
            <OffCanvasMenuContent>
                <OffCanvasMenuClose>
                    <Icon className="text-lg" name="close" />
                </OffCanvasMenuClose>
                {renderProfileSection()}
                <Hr className="my-0 mb-2" />
                {renderSearchMenuItem()}
                {renderForumPickerMenuItem()}
                {renderMyBoardsMenuItem()}
                {renderStakingButton()}
                {renderAccountMenuItem()}
                {renderNetworkAdminDashboard()}
                {renderDarkModeToggle()}
                <Hr className="my-2" />
                {renderDomainMainMenu()}

                <div className="px-5 pt-2 space-x-3 text-xs">
                    {renderPrivacyPolicyLink()}
                    {renderTermsOfUseLink()}
                    {renderFaqLink()}
                </div>
                <div className="px-5">
                    <AppVersion className="text-gray-500 dark:text-gray-500 text-[10px]" />
                </div>
            </OffCanvasMenuContent>
        </OffCanvasMenu>
    );
};

export default memo(MainMenu);
